import i18n from '@assets/i18n/config';
import employmentInfo from '@assets/i18n/en/employmentInfo.json';
import {
  type ComplianceStates,
  type EmploymentStatus,
  EntityTypes,
  type FatcaStatus,
  type GoalRiskLevels,
  type InvestmentKnowledge,
  type MaritalStatus,
  type MethodOfCommunication,
  type OrganizationUserEntityRelationTypes,
  RelatedEntityTypes,
  type Relation,
  TaxIdTypes,
  type TrustTypes,
  type UserSourceOfFunds,
  type UserState,
} from '@generated/graphql';
import { CountryCodes, type Languages } from '@onevesthq/ov-enums';
import { enumValues } from '@utils/enumValues';
import type { Account } from './account';
import type { AccountGroup } from './accountGroup';
import type { BankAccount } from './bankAccount';
import type { BillingSchedule } from './billingSchedule';
import type { ClientGroup } from './clientGroup';
import type { CustomFieldValue } from './customField';
import type { FeeTier } from './feeTier';
import type { ForeignTaxInformation } from './foreignTaxInformation';
import type { IncompleteFormAgreement } from './formAgreements';
import type { FraudRisk } from './fraudRisk';
import type { Goal, SubAccount } from './goalSubAccountTypes';
import type { IdVerification } from './IdVerification';
import type { Organization } from './organization';
import type { OrganizationUser } from './organizationUser';
import type { PhysicalAddress } from './physicalAddress';
import type { Role } from './role';
import type { TranslatedString } from './shared';
import type { Statistics } from './stats';

export type RelatedEntity = {
  entity: User;
  relation: RelatedEntityTypes;
};

export type RelatedEntityInput = {
  entityId: string;
  relation: RelatedEntityTypes;
};

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  OTHER = 'OTHER',
}

export const getRelatedEntityTypes = (
  entityType: EntityTypes | string,
): RelatedEntityTypes[] => {
  let candidateRelatedEntityTypes: RelatedEntityTypes[];
  switch (entityType) {
    case EntityTypes.Individual:
      candidateRelatedEntityTypes = [
        RelatedEntityTypes.Spouse,
        RelatedEntityTypes.Child,
        RelatedEntityTypes.Parent,
        RelatedEntityTypes.Sibling,
        RelatedEntityTypes.Grandchild,
        RelatedEntityTypes.Grandparent,
        RelatedEntityTypes.NieceNephew,
        RelatedEntityTypes.Friend,
        RelatedEntityTypes.PowerOfAttorney,
        RelatedEntityTypes.ThirdParty,
        RelatedEntityTypes.TrustedContact,
        RelatedEntityTypes.Other,
      ];
      break;
    case EntityTypes.Trust:
      candidateRelatedEntityTypes = [
        RelatedEntityTypes.Trustee,
        RelatedEntityTypes.BeneficialOwner,
        RelatedEntityTypes.Grantor,
        RelatedEntityTypes.Protector,
        RelatedEntityTypes.Settlor,
        RelatedEntityTypes.ThirdParty,
        RelatedEntityTypes.PowerOfAttorney,
        RelatedEntityTypes.TrustedContact,
      ];
      break;
    case EntityTypes.InvestmentFund:
    case EntityTypes.Partnership:
    case EntityTypes.PrivateCompany:
    case EntityTypes.PubliclyListedEntity:
    case EntityTypes.RegulatedEntity:
    case EntityTypes.SoleProprietorship:
    case EntityTypes.Other:
      candidateRelatedEntityTypes = [
        RelatedEntityTypes.Director,
        RelatedEntityTypes.AuthorizedIndividual,
        RelatedEntityTypes.BeneficialOwner,
        RelatedEntityTypes.ThirdParty,
        RelatedEntityTypes.PowerOfAttorney,
        RelatedEntityTypes.SeniorOfficer,
        RelatedEntityTypes.TrustedContact,
      ];
      break;
    default:
      candidateRelatedEntityTypes = enumValues(RelatedEntityTypes);
  }
  return candidateRelatedEntityTypes;
};

export type EntityOrganizationUser = {
  organizationUser?: OrganizationUser;
  readOnly?: boolean;
  relation?: OrganizationUserEntityRelationTypes;
  role?: Role;
  accounts?: Account[];
};

export type UserFeeSetting = {
  feeTier?: FeeTier;
  billingSchedule?: BillingSchedule;
  applicableFeeTier?: FeeTier;
  applicableBillingSchedule?: BillingSchedule;
};

export type User = {
  id: string;
  type: EntityTypes;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  entityName?: string;
  typeOfTrust?: TrustTypes;
  primaryEmail?: string;
  phone?: string;
  dateOfBirth?: string;
  establishedDate?: string;
  createdAt?: string;
  completedAt?: string;
  suitabilityScore?: number;
  maritalStatus?: MaritalStatus;
  numberOfDependents?: number;
  employmentStatus?: EmploymentStatus;
  jobTitle?: string;
  companyType?: string;
  studentAreaOfStudy?: string;
  sin?: string;
  isOfficerOfPublicCompany?: boolean;
  isOwnerOfPublicCompany?: boolean;
  isMemberOfIiroc?: boolean;
  politicallyExposedForeignPerson?: boolean;
  politicallyExposedDomesticPerson?: boolean;
  headOfInternationalOrganization?: boolean;
  closeAssociateOfPEP?: boolean;
  isRelatedToAdvisor?: boolean;
  complianceState?: ComplianceStates;
  complianceIssueSource?: string;
  complianceIssueSourceTranslated?: TranslatedString;
  annualIncomeCents?: number;
  annualIncomeStr?: string;
  financialLiquidAssetsCents?: number;
  financialFixedAssetsCents?: number;
  totalDebtCents?: number;
  annualDebtPaymentsCents?: number;
  isAccreditedInvestor?: boolean;
  investmentKnowledge?: InvestmentKnowledge;
  riskQuestion1?: GoalRiskLevels;
  riskQuestion2?: GoalRiskLevels;
  statistics?: Statistics;
  custodianStatistics?: Statistics;
  customFields?: CustomFieldValue[];
  subAccounts: SubAccount[];
  accountGroups?: AccountGroup[];
  accounts: Account[];
  goals: Goal[];
  organization: Organization;
  physicalAddress?: PhysicalAddress;
  iDVerified?: boolean;
  iDCheckCompleted?: boolean;
  iDCheckRetrigger?: boolean;
  affiliateOnly?: boolean;
  state: UserState;
  language?: Languages;
  preferredMethodOfCommunication?: MethodOfCommunication;
  latestIdVerification?: IdVerification;
  feeTier?: FeeTier;
  applicableFeeTier?: FeeTier;
  billingSchedule?: BillingSchedule;
  applicableBillingSchedule?: BillingSchedule;
  feeSetting?: UserFeeSetting;
  citizenships?: string[];
  countryOfTaxResidence?: CountryCodes;
  foreignTaxInformation?: ForeignTaxInformation[];
  foreignTaxExists?: boolean;
  lastSuitabilityReviewAt?: string;
  incompleteFields?: string[];
  ssSymbols?: string[];
  insiderSymbols?: string[];
  powerOfAttorneyGranted?: boolean;
  fraudRisk?: FraudRisk;
  incompleteFormAgreements?: IncompleteFormAgreement[];
  allIncompleteFormAgreements?: IncompleteFormAgreement[];
  trustedContactPerson?: {
    name: string;
    email: string;
    phone: string;
    physicalAddress: PhysicalAddress;
    relation: Relation;
  };
  bankAccounts?: BankAccount[];
  households?: ClientGroup[];
  employerName?: string;
  employmentSituation?: string;
  employmentSituationOtherDescription?: string;
  employerCompanyAddress?: PhysicalAddress;
  sourceOfWealth?: string[];
  sourceOfFunds?: UserSourceOfFunds[];
  uniqueCircumstances?: string;
  sourceOfFundsOtherDescription?: string;
  sourceOfWealthOtherDescription?: string;
  readyToSignAgreement?: boolean;
  spouseSin?: string;
  spouseDateOfBirth?: string;
  dateOfDeath?: string;
  spouseFirstName?: string;
  spouseMiddleName?: string;
  spouseLastName?: string;
  spouseJobTitle?: string;
  spouseAreaOfStudy?: string;
  spouseCompanyType?: string;
  spouseEmploymentType?: string;
  spouseEmploymentStatus?: EmploymentStatus;
  spouseEmployerName?: string;
  spouseEmployerCompanyAddress?: PhysicalAddress;
  spouseEmployerCompanyPhone?: string;
  spouseEmail?: string;
  spouseAddress?: PhysicalAddress;
  spouseTaxId?: string;
  spouseTaxIdDisclosed?: string;
  spouseTaxIdType?: TaxIdTypes;
  spouseAnnualIncomeCents?: number;
  spouseAnnualIncomeStr?: string;
  inProvinceSince?: string;
  taxId?: string;
  taxIdDisclosed?: string;
  taxIdType?: TaxIdTypes;
  taxIdExists?: boolean;
  sinExists?: boolean;
  relatedEntities?: RelatedEntity[];
  countryOfRegistration?: string;
  registrationNumber?: string;
  charityRegistrationNumber?: string;
  taxNumber?: string;
  yearEnd?: string;
  isNonProfit?: boolean;
  receivesDonationsFromPublic?: boolean;
  isRegisteredWithCRA?: boolean;
  isEntityRegulated?: boolean;
  regulatorName?: string;
  firmNumber?: string;
  isReportingIssuer?: boolean;
  issuerName?: string;
  issuerTicker?: string;
  issuerExchange?: string;
  isAFinancialEntity?: boolean;
  isAffiliateOfFinancialEntity?: boolean;
  instructionsFromFinancialEntity?: boolean;
  isPublicBody?: boolean;
  isLargeCorporation?: boolean;
  isForThirdParty?: boolean;
  gender?: string;
  accessibleBy?: EntityOrganizationUser[];
  isVulnerablePerson?: boolean;
  isOwnerOfEntity?: boolean;
  fatcaStatus?: FatcaStatus;
  fatcaStatusOtherDescription?: string;
  hasGuarantor?: boolean;
  otherPersonGuaranteeNames?: string;
  tradingAuthorityOverAccountGranted?: boolean;
  tradingAuthorityName?: string;
  discretionOverAccountGranted?: boolean;
  advisorInterest?: boolean;
  advisorInterestDetail?: string;
  investmentFirmTrade?: boolean;
  investmentFirmTradeName?: string;
  useBorrowedMoney?: boolean;
  thirdPartyName?: string;
  pepReason?: string;
  __typename?: string;
  nextSuitabilityReviewAt?: string;
  discretionOverAccountGrantedReason?: string;
};

type EmploymentDetail = {
  id: string;
  title: string;
};

export type Profession = EmploymentDetail;
export type JobTitle = EmploymentDetail;
export type AreaOfStudy = EmploymentDetail;

export const getProfessions = (): Profession[] => {
  const professionsData = employmentInfo.professionOptions;

  return Object.keys(professionsData).map(
    (professionId: string): Profession => ({
      id: professionId,
      title: i18n.t(`employmentInfo:professionOptions.${professionId}.title`),
    }),
  );
};

export const getJobTitles = ({
  professionId,
  professionTitle,
}: {
  professionId?: keyof typeof employmentInfo.professionOptions;
  professionTitle?: string;
}): JobTitle[] => {
  let jobs: JobTitle[] = [];
  // Return jobs list filtered in the following sequence:
  // professionId > professionTitle > All Jobs
  const professionKey =
    professionId ??
    Object.keys(employmentInfo.professionOptions).find((key) => {
      const option =
        employmentInfo.professionOptions[
          key as keyof typeof employmentInfo.professionOptions
        ];
      const localizedTitle = i18n.t(
        `employmentInfo:professionOptions.${key}.title`,
      );
      return (
        professionTitle !== undefined &&
        (option.title === professionTitle || localizedTitle === professionTitle)
      );
    }) ??
    undefined;
  if (professionKey) {
    const professionJobs =
      employmentInfo.professionOptions?.[
        professionKey as keyof typeof employmentInfo.professionOptions
      ]?.titles;
    jobs = Object.keys(professionJobs).map(
      (jobId: string): JobTitle => ({
        id: jobId,
        title: i18n.t(
          `employmentInfo:professionOptions.${professionKey}.titles.${jobId}`,
        ),
      }),
    );
  } else {
    // If no profession info is provided, return all job titles available across all professions
    jobs = Object.keys(employmentInfo.professionOptions).reduce(
      (acc: JobTitle[], curProfessionId: string): JobTitle[] => {
        const curProfessionJobs =
          employmentInfo.professionOptions?.[
            curProfessionId as keyof typeof employmentInfo.professionOptions
          ]?.titles;
        acc.push(
          ...Object.keys(curProfessionJobs).map(
            (jobId: string): JobTitle => ({
              id: jobId,
              title: i18n.t(
                `employmentInfo:professionOptions.${curProfessionId}.titles.${jobId}`,
              ),
            }),
          ),
        );
        return acc;
      },
      [],
    );
  }
  return jobs;
};

export const getAreasOfStudy = (): AreaOfStudy[] => {
  const areasOfStudyData = employmentInfo.areasOfStudy;

  return Object.keys(areasOfStudyData).map(
    (areaOfStudyId: string): AreaOfStudy => ({
      id: areaOfStudyId,
      title: i18n.t(`employmentInfo:areasOfStudy.${areaOfStudyId}`),
    }),
  );
};

export const SUITABILITY_REVIEW_VALIDITY_MONTHS = 12;

export const encryptedTaxIdPlaceholder = (taxIdType?: TaxIdTypes): string =>
  taxIdFormattingMask(taxIdType).replaceAll('#', '*');

export const taxIdFormattingMask = (taxIdType?: TaxIdTypes): string => {
  if (taxIdType === TaxIdTypes.Sin || taxIdType === TaxIdTypes.Bn)
    return '### ### ###';
  if (taxIdType === TaxIdTypes.Ssn || taxIdType === TaxIdTypes.Itin)
    return '###-##-####';
  if (taxIdType === TaxIdTypes.Ein) return '##-########';
  return '#########';
};

export const taxIdTypeFormatType = (
  type?: TaxIdTypes,
): '3-3-3' | '3-2-4' | '2-7' | undefined => {
  if (type === TaxIdTypes.Sin || type === TaxIdTypes.Bn) return '3-3-3';
  if (type === TaxIdTypes.Ssn || type === TaxIdTypes.Itin) return '3-2-4';
  if (type === TaxIdTypes.Ein) return '2-7';
  return undefined;
};

export const eligibleTaxIdTypes = (
  country: CountryCodes,
  isIndividual: boolean,
): TaxIdTypes[] => {
  let types: TaxIdTypes[] = [];

  if (country === CountryCodes.CA) {
    types = isIndividual ? [TaxIdTypes.Sin] : [TaxIdTypes.Bn];
  } else if (country === CountryCodes.US) {
    types = isIndividual
      ? [TaxIdTypes.Ssn, TaxIdTypes.Itin]
      : [TaxIdTypes.Ssn, TaxIdTypes.Itin, TaxIdTypes.Ein];
  } else {
    types = [TaxIdTypes.Generic];
  }

  return types;
};
